import React, { useId, useState, useEffect, useRef } from "react";

interface AIProcessingButtonFrameProps {
  active?: boolean;
  children: React.ReactNode;
  className?: string;
  width?: string;
  height?: string;
  targetSelector?: string;
}

function AIProcessingButtonFrame({
  active = false,
  children,
  className = "",
  width,
  height,
  targetSelector,
}: AIProcessingButtonFrameProps) {
  const gradientId = useId().replace(/:/g, "");
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [btnRadius, setBtnRadius] = useState(20);
  const [svgStyle, setSvgStyle] = useState<React.CSSProperties>({
    position: "absolute",
    display: "none",
  });
  const [flashStyle, setFlashStyle] = useState<React.CSSProperties>({
    position: "absolute",
    display: "none",
  });

  const wrapperClassName = `ai-processing-shell${active ? " ai-processing-shell--active" : ""}${
    className ? ` ${className}` : ""
  }`;

  // Track button position, size, and border radius when active is true
  useEffect(() => {
    if (!containerRef.current || !active) {
      setSvgStyle({ position: "absolute", display: "none" });
      setFlashStyle({ position: "absolute", display: "none" });
      return undefined;
    }

    const targetEl = containerRef.current.querySelector<HTMLElement>(targetSelector || "button");
    if (!targetEl) return undefined;

    const updatePosition = () => {
      const radiusStr = window.getComputedStyle(targetEl).borderRadius;
      const maxRadius = targetEl.offsetHeight / 2;
      let radiusVal = 20;
      if (radiusStr.includes("%")) {
        radiusVal = maxRadius;
      } else {
        radiusVal = Math.min(parseFloat(radiusStr) || 20, maxRadius);
      }

      setBtnRadius(radiusVal);

      setSvgStyle({
        position: "absolute",
        left: targetEl.offsetLeft,
        top: targetEl.offsetTop,
        width: targetEl.offsetWidth,
        height: targetEl.offsetHeight,
        pointerEvents: "none",
        zIndex: 10,
        overflow: "visible",
      });

      setFlashStyle({
        position: "absolute",
        left: targetEl.offsetLeft + 1,
        top: targetEl.offsetTop + 1,
        width: targetEl.offsetWidth - 2,
        height: targetEl.offsetHeight - 2,
        borderRadius: radiusVal,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 3,
      });

      setDimensions({
        width: targetEl.offsetWidth,
        height: targetEl.offsetHeight,
      });
    };

    updatePosition();

    // Set up ResizeObserver to track layout changes
    const observer = new ResizeObserver(updatePosition);
    observer.observe(targetEl);

    // Also update on window resize
    window.addEventListener("resize", updatePosition);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
    };
  }, [active, targetSelector]);

  const rectWidth = Math.max(0, dimensions.width - 2);
  const rectHeight = Math.max(0, dimensions.height - 2);
  const rectRadius = Math.min(btnRadius, rectWidth / 2, rectHeight / 2);
  const pathLength = Math.max(0, 2 * rectWidth + 2 * rectHeight - 8 * rectRadius + 2 * Math.PI * rectRadius);
  const dashLength = Math.min(Math.max(pathLength * 0.22, 34), pathLength * 0.34);
  const gapLength = Math.max(0, pathLength - dashLength);
  const ringStyle = {
    ...svgStyle,
    ["--ai-processing-path-length" as any]: `${pathLength}px`,
    ["--ai-processing-dash-length" as any]: `${dashLength}px`,
    ["--ai-processing-gap-length" as any]: `${gapLength}px`,
    ["--ai-processing-orbit-end" as any]: `${-pathLength}px`,
  };

  return (
    <div
      ref={containerRef}
      className={wrapperClassName}
      data-button-processing={active ? "true" : undefined}
      style={{
        position: "relative",
        display: "inline-block",
        width,
        height,
        ["--button-width" as any]: `${dimensions.width}px`,
      }}
    >
      {active && dimensions.width > 0 && dimensions.height > 0 && (
        <>
          <svg
            className="ai-processing-shell__ring"
            style={ringStyle}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2={dimensions.width} y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="var(--primary-color)" />
                <stop offset="50%" stopColor="var(--secondary-color)" />
                <stop offset="100%" stopColor="var(--primary-color)" />
              </linearGradient>
            </defs>
            <rect
              className="ai-processing-shell__track"
              x="1"
              y="1"
              width={rectWidth}
              height={rectHeight}
              rx={rectRadius}
              ry={rectRadius}
            />
            <rect
              className="ai-processing-shell__path ai-processing-shell__path--glow"
              x="1"
              y="1"
              width={rectWidth}
              height={rectHeight}
              rx={rectRadius}
              ry={rectRadius}
              stroke={`url(#${gradientId})`}
            />
            <rect
              className="ai-processing-shell__path"
              x="1"
              y="1"
              width={rectWidth}
              height={rectHeight}
              rx={rectRadius}
              ry={rectRadius}
              stroke={`url(#${gradientId})`}
            />
          </svg>

          <div className="ai-processing-shell__flash" style={flashStyle} aria-hidden="true">
            <div className="ai-processing-shell__flash-band"></div>
          </div>
        </>
      )}
      {children}
    </div>
  );
}

export default AIProcessingButtonFrame;
